/**
 * usePermissions - React Hook for Permission System
 * 
 * Provides reactive permission checks in components.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  can,
  require as requirePerm,
  shouldMask,
  getVisibleActions,
  getPrimaryRole,
  getSession,
  setSessionCapabilities,
  setSnapshotLockState,
  getSnapshotLockState,
  checkPermission,
  getRoleDisplayInfo,
  hasRole as hasRoleUtil,
  isBoardView as isBoardViewUtil,
  isLegal as isLegalUtil,
  isCompliance as isComplianceUtil
} from '../lib/permissions';
import { apiFetch } from '../services/api_service';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Main permissions hook
 */
export function usePermissions() {
  const [session, setSession] = useState(null);
  const [snapshotLock, setSnapshotLock] = useState({ isLocked: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch session capabilities on mount
  useEffect(() => {
    fetchSessionCapabilities();
  }, []);

  const fetchSessionCapabilities = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        // Create demo session for unauthenticated users
        const demoSession = createDemoSession();
        setSessionCapabilities(demoSession);
        setSession(demoSession);
        setLoading(false);
        return;
      }

      const response = await apiFetch(`/session/capabilities`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response || response._error) {
        // Fall back to demo session
        const demoSession = createDemoSession();
        setSessionCapabilities(demoSession);
        setSession(demoSession);
      } else {
        setSessionCapabilities(response);
        setSession(response);
      }
    } catch (err) {
      console.error('Failed to fetch session capabilities:', err);
      // Fall back to demo session
      const demoSession = createDemoSession();
      setSessionCapabilities(demoSession);
      setSession(demoSession);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Update snapshot lock state
  const updateSnapshotLock = useCallback((lockState) => {
    setSnapshotLockState(lockState);
    setSnapshotLock(lockState);
  }, []);

  // Permission check function (memoized)
  const checkCan = useCallback((action, resource, context) => {
    return can(action, resource, context);
  }, []);

  // Require function - returns boolean
  const checkRequire = useCallback((action, resource, context) => {
    return requirePerm(action, resource, context);
  }, []);

  // Check if should mask field
  const checkMask = useCallback((fieldType, context) => {
    return shouldMask(fieldType, context);
  }, []);

  // Get visible actions for resource
  const getActions = useCallback((resource) => {
    return getVisibleActions(resource);
  }, []);

  // Get primary role
  const primaryRole = useMemo(() => {
    return getPrimaryRole();
  }, [session]);

  // Role display info
  const roleInfo = useMemo(() => {
    if (!primaryRole) return null;
    return getRoleDisplayInfo(primaryRole);
  }, [primaryRole]);

  // Check if user has any of specified roles
  const hasRole = useCallback((roles) => {
    return hasRoleUtil(roles);
  }, [session]);

  // Check if user is in board view (read-only leadership)
  const isBoardView = useMemo(() => {
    return isBoardViewUtil();
  }, [session]);

  // Check if user is legal
  const isLegal = useMemo(() => {
    return isLegalUtil();
  }, [session]);

  // Check if user is compliance
  const isCompliance = useMemo(() => {
    return isComplianceUtil();
  }, [session]);

  // Check if user is audit
  const isAudit = useMemo(() => {
    return hasRoleUtil(['INTERNAL_AUDIT', 'EXTERNAL_AUDITOR']);
  }, [session]);

  // Check if user is sustainability
  const isSustainability = useMemo(() => {
    return hasRoleUtil(['SUSTAINABILITY_LEAD', 'SUSTAINABILITY_ANALYST']);
  }, [session]);

  // Check snapshot is locked
  const isSnapshotLocked = useMemo(() => {
    return snapshotLock.isLocked;
  }, [snapshotLock]);

  return {
    session,
    loading,
    error,
    snapshotLock,
    isSnapshotLocked,
    can: checkCan,
    require: checkRequire,
    shouldMask: checkMask,
    getVisibleActions: getActions,
    primaryRole,
    roleInfo,
    hasRole,
    isBoardView,
    isLegal,
    isCompliance,
    isAudit,
    isSustainability,
    refetch: fetchSessionCapabilities,
    updateSnapshotLock
  };
}

/**
 * Create demo session for development/unauthenticated users
 */
function createDemoSession() {
  const savedUser = localStorage.getItem('user');
  let role = 'COMPLIANCE_OWNER';
  let userId = 'demo_user';
  
  if (savedUser) {
    try {
      const user = JSON.parse(savedUser);
      userId = user.id || user._id || 'demo_user';
      // Map basic roles to enterprise roles
      if (user.role === 'admin') {
        role = 'COMPLIANCE_OWNER';
      } else if (user.role === 'employee') {
        role = 'COMPLIANCE_ANALYST';
      } else if (user.role === 'auditor') {
        role = 'INTERNAL_AUDIT';
      } else if (user.role === 'client') {
        role = 'SUSTAINABILITY_LEAD';
      }
    } catch (e) {
      // Use default
    }
  }

  return {
    userId,
    roles: [role],
    permissions: [
      // Exposure & Liability
      'EXPOSURE:VIEW',
      'EXPOSURE:DRILL',
      'LIABILITY:VIEW',
      'LIABILITY:DRILL',
      // Audit Pack
      'AUDIT_PACK:VIEW',
      'AUDIT_PACK:GENERATE',
      'AUDIT_PACK:EXPORT',
      // Obligation
      'OBLIGATION:VIEW',
      'OBLIGATION:TRANSITION',
      // Evidence
      'EVIDENCE:VIEW',
      'EVIDENCE:UPLOAD',
      'EVIDENCE:VALIDATE',
      // Impact
      'IMPACT:VIEW',
      'IMPACT:CREATE',
      // Claim
      'CLAIM:VIEW',
      // Coverage
      'COVERAGE:VIEW',
      // Model & Event
      'MODEL:VIEW',
      'EVENT:VIEW',
      // Export
      'EXPORT:PROOF_BUNDLE',
      // Supplier & Entity
      'SUPPLIER:VIEW',
      'LEGAL_ENTITY:VIEW',
      // Snapshot
      'SNAPSHOT:VIEW'
    ],
    scope: {
      legalEntityIds: [], // Empty = all entities
      boundaryIds: [],    // Empty = all boundaries
      groupIds: [],
      supplierTierMax: 'TIER_3',
      maxConfidentiality: 3
    },
    sod: {
      cannotValidateEvidenceUploadedBySelf: true,
      cannotApproveOwnWork: true
    }
  };
}

/**
 * Hook for checking specific permission
 */
export function useCanPerform(action, resource, context) {
  const { can, loading } = usePermissions();
  
  const result = useMemo(() => {
    if (loading) return { allowed: false, loading: true };
    return { ...can(action, resource, context), loading: false };
  }, [can, action, resource, context, loading]);

  return result;
}

/**
 * Hook for gated button rendering
 */
export function useGatedAction(action, resource, context) {
  const { loading } = usePermissions();

  const permission = useMemo(() => {
    if (loading) {
      return {
        allowed: false,
        loading: true,
        showDisabled: true,
        showApprovalPath: false,
        tooltip: 'Loading permissions...'
      };
    }
    return checkPermission(action, resource, context);
  }, [action, resource, context, loading]);

  return permission;
}

/**
 * Hook for snapshot lock state
 */
export function useSnapshotLock() {
  const { snapshotLock, updateSnapshotLock, isLegal } = usePermissions();
  
  const canLock = useMemo(() => {
    return isLegal && !snapshotLock.isLocked;
  }, [isLegal, snapshotLock]);

  const canUnlock = useMemo(() => {
    // Unlock requires dual approval - simplified for now
    return isLegal && snapshotLock.isLocked;
  }, [isLegal, snapshotLock]);

  return {
    ...snapshotLock,
    canLock,
    canUnlock,
    setLock: updateSnapshotLock
  };
}

export default usePermissions;
